/**
 * Document Status Management Utilities
 * Centralized status definitions and helpers for Baobab Connect
 */

// Status definitions by document type
export const DEVIS_STATUSES = {
  BROUILLON: { value: 'brouillon', label: 'Brouillon', color: 'bg-gray-100 text-gray-800' },
  ENVOYE: { value: 'envoyé', label: 'Envoyé', color: 'bg-blue-100 text-blue-800' },
  ACCEPTE: { value: 'accepté', label: 'Accepté', color: 'bg-green-100 text-green-800' },
  REFUSE: { value: 'refusé', label: 'Refusé', color: 'bg-red-100 text-red-800' },
  EXPIRE: { value: 'expiré', label: 'Expiré', color: 'bg-orange-100 text-orange-800' },
  CONVERTI: { value: 'converti_en_bdc_facture', label: 'Converti en BDC/Facture', color: 'bg-purple-100 text-purple-800' }
};

export const BON_COMMANDE_STATUSES = {
  BROUILLON: { value: 'brouillon', label: 'Brouillon', color: 'bg-gray-100 text-gray-800' },
  VALIDE: { value: 'validé', label: 'Validé', color: 'bg-green-100 text-green-800' },
  ENVOYE_CLIENT: { value: 'envoyé_au_client', label: 'Envoyé au client', color: 'bg-blue-100 text-blue-800' },
  ACCEPTE: { value: 'accepté', label: 'Accepté', color: 'bg-green-100 text-green-800' },
  EN_COURS: { value: 'en_cours', label: 'En cours', color: 'bg-yellow-100 text-yellow-800' },
  TERMINE: { value: 'terminé', label: 'Terminé', color: 'bg-green-100 text-green-800' },
  FACTURE: { value: 'facturé', label: 'Facturé', color: 'bg-purple-100 text-purple-800' },
  ANNULE: { value: 'annulé', label: 'Annulé', color: 'bg-red-100 text-red-800' }
};

export const FACTURE_STATUSES = {
  BROUILLON: { value: 'brouillon', label: 'Brouillon', color: 'bg-gray-100 text-gray-800' },
  ENVOYEE: { value: 'envoyée', label: 'Envoyée', color: 'bg-blue-100 text-blue-800' },
  VALIDEE: { value: 'validée', label: 'Validée', color: 'bg-green-100 text-green-800' },
  EN_ATTENTE_PAIEMENT: { value: 'en_attente_de_paiement', label: 'En attente de paiement', color: 'bg-yellow-100 text-yellow-800' },
  PAYEE_PARTIELLEMENT: { value: 'payée_partiellement', label: 'Payée partiellement', color: 'bg-orange-100 text-orange-800' },
  PAYEE_INTEGRALEMENT: { value: 'payée_intégralement', label: 'Payée intégralement', color: 'bg-green-100 text-green-800' },
  EN_RETARD: { value: 'en_retard', label: 'En retard', color: 'bg-red-100 text-red-800' },
  ANNULEE_AVOIR: { value: 'annulée_avoir_émis', label: 'Annulée / Avoir émis', color: 'bg-gray-100 text-gray-800' }
};

export const PAYMENT_STATUSES = {
  NON_PAYE: { value: 'non_payé', label: 'Non payé', color: 'bg-red-100 text-red-800' },
  ACOMPTE_RECU: { value: 'acompte_reçu', label: 'Acompte reçu', color: 'bg-orange-100 text-orange-800' },
  EN_COURS_TRAITEMENT: { value: 'en_cours_de_traitement', label: 'En cours de traitement', color: 'bg-blue-100 text-blue-800' },
  PAYE_PARTIELLEMENT: { value: 'payé_partiellement', label: 'Payé partiellement', color: 'bg-orange-100 text-orange-800' },
  PAYE_INTEGRALEMENT: { value: 'payé_intégralement', label: 'Payé intégralement', color: 'bg-green-100 text-green-800' },
  EN_RETARD: { value: 'en_retard', label: 'En retard', color: 'bg-red-100 text-red-800' },
  LITIGE: { value: 'litige', label: 'Litige', color: 'bg-red-100 text-red-800' },
  ANNULE_AVOIR: { value: 'annulé_avoir', label: 'Annulé / Avoir', color: 'bg-gray-100 text-gray-800' }
};

/**
 * Get status configuration by document type
 * @param {string} documentType - Type of document (devis, bon_commande, facture)
 * @returns {Object} Status configuration object
 */
export const getStatusesByDocumentType = (documentType) => {
  const statusMap = {
    'devis': DEVIS_STATUSES,
    'bon_commande': BON_COMMANDE_STATUSES,
    'facture': FACTURE_STATUSES
  };
  
  return statusMap[documentType] || {};
};

/**
 * Get status display configuration
 * @param {string} statusValue - Status value to look up
 * @param {string} documentType - Type of document
 * @returns {Object} Status configuration with label and color
 */
export const getStatusConfig = (statusValue, documentType) => {
  const statuses = getStatusesByDocumentType(documentType);
  
  // Find matching status
  const statusEntry = Object.values(statuses).find(s => s.value === statusValue);
  
  if (statusEntry) {
    return statusEntry;
  }
  
  // Fallback: try to match in payment statuses
  const paymentStatus = Object.values(PAYMENT_STATUSES).find(s => s.value === statusValue);
  if (paymentStatus) {
    return paymentStatus;
  }
  
  // Default fallback
  return {
    value: statusValue,
    label: statusValue || 'Non défini',
    color: 'bg-gray-100 text-gray-800'
  };
};

/**
 * Get payment status display configuration
 * @param {string} paymentStatusValue - Payment status value
 * @returns {Object} Payment status configuration
 */
export const getPaymentStatusConfig = (paymentStatusValue) => {
  const statusEntry = Object.values(PAYMENT_STATUSES).find(s => s.value === paymentStatusValue);
  
  if (statusEntry) {
    return statusEntry;
  }
  
  // Default fallback
  return {
    value: paymentStatusValue,
    label: paymentStatusValue || 'Non défini',
    color: 'bg-gray-100 text-gray-800'
  };
};

/**
 * Get all document statuses as an array for a specific type
 * @param {string} documentType - Type of document
 * @returns {Array} Array of status objects
 */
export const getDocumentStatusesArray = (documentType) => {
  const statuses = getStatusesByDocumentType(documentType);
  return Object.values(statuses);
};

/**
 * Get all payment statuses as an array
 * @returns {Array} Array of payment status objects
 */
export const getPaymentStatusesArray = () => {
  return Object.values(PAYMENT_STATUSES);
};

/**
 * Legacy support: Map old status values to new ones
 */
export const mapLegacyStatus = (oldStatus, documentType) => {
  const legacyMap = {
    'acceptée': 'accepté',
    'envoyé': 'envoyé',
    'en_cours': 'en_cours',
    'payé': 'payé_intégralement',
    'partiellement_payé': 'payée_partiellement',
    'encaissé': 'payée_intégralement',
    'partiellement_encaissé': 'payée_partiellement'
  };
  
  return legacyMap[oldStatus] || oldStatus;
};

export default {
  DEVIS_STATUSES,
  BON_COMMANDE_STATUSES,
  FACTURE_STATUSES,
  PAYMENT_STATUSES,
  getStatusesByDocumentType,
  getStatusConfig,
  getPaymentStatusConfig,
  getDocumentStatusesArray,
  getPaymentStatusesArray,
  mapLegacyStatus
};
